import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { useCallback, useState, type ReactNode } from "react";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line, Path, Rect } from "react-native-svg";
import { SafeAreaView } from "react-native-safe-area-context";

import { signOutToHome } from "../../lib/auth";
import { COLORS, FONTS, SPACING, STROKES } from "../../lib/brand";
import { LogoMark, LogoWordmark } from "../brand/Logo";
import { PANE_THEMES } from "../brand/paneThemes";
import { useShellResponsive } from "../../lib/useShellResponsive";
import { HamburgerButton, ShellMobileDrawer } from "../ShellMobileDrawer";

// Judge chrome is locked to the Judge pane theme so editing /brand (Judge)
// propagates here. Local const names kept so the JSX below is untouched —
// only the source of each value changed.
const THEME = PANE_THEMES.Judge;
const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;

const SIDEBAR_W = 220;
const TOPBAR_H = 52;

const W = "rgba(255,255,255,1)";
const BORDER = "rgba(255,255,255,0.07)";

export type JudgeNavKey = "assignments" | "profile" | "settings";

export type JudgeIdentity = {
  fullName: string;
  /** Organization line — empty string when missing (no fallback). */
  orgLine: string;
  initials: string;
  /** Public URL from user_profiles.avatar_url; null when none set. */
  avatarUrl: string | null;
};

export type JudgeShellProps = {
  children: ReactNode;
  topbarTitle: string;
  /** Auto-derived from pathname when omitted. */
  activeKey?: JudgeNavKey;
  identity: JudgeIdentity;
};

export function JudgeShell({ children, topbarTitle, activeKey, identity }: JudgeShellProps) {
  const router = useRouter();
  const { isMobile, showSidebar, drawerOpen, openDrawer, closeDrawer } =
    useShellResponsive();

  const iconColor = (key: JudgeNavKey) =>
    activeKey === key ? LINEN : "rgba(237,232,223,0.42)";

  const navTo = useCallback(
    (href: Href) => {
      closeDrawer();
      router.push(href);
    },
    [closeDrawer, router],
  );

  const sidebarBody = (
    <>
      {/* Logo header */}
      <Pressable
        onPress={() => navTo("/judge" as Href)}
              style={[
                {
                  height: TOPBAR_H,
                  paddingHorizontal: 18,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  borderBottomWidth: STROKES.hairline,
                  borderBottomColor: "rgba(181,135,58,0.12)",
                },
              ]}
            >
              <LogoMark size={28} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
              <View style={{ minWidth: 0, flexShrink: 1 }}>
                <LogoWordmark
                  numberOfLines={1}
                  fontSize={17}
                  letterSpacing={3}
                  color={LINEN}
                  accent={AMBER}
                />
                <Text
                  numberOfLines={1}
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 8.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "rgba(237,232,223,0.28)",
                    marginTop: 1,
                  }}
                >
                  Judge Portal
                </Text>
              </View>
            </Pressable>

            <View style={{ height: 12 }} />

            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: SPACING.sm }}
              showsVerticalScrollIndicator={false}
            >
              <SidebarNavItem
                label="Assignments"
                active={activeKey === "assignments"}
                onPress={() => navTo("/judge" as Href)}
                icon={<IconClipboard color={iconColor("assignments")} />}
              />
              <SidebarNavItem
                label="Profile"
                active={activeKey === "profile"}
                onPress={() => navTo("/judge/profile" as Href)}
                icon={<IconPerson color={iconColor("profile")} />}
              />
              <SidebarNavItem
                label="Settings"
                active={activeKey === "settings"}
                onPress={() => navTo("/judge/settings" as Href)}
                icon={<IconSettings color={iconColor("settings")} />}
              />
            </ScrollView>

            {/* Footer */}
            <View
              style={{
                paddingHorizontal: 18,
                paddingVertical: SPACING.sm,
                borderTopWidth: STROKES.hairline,
                borderTopColor: "rgba(181,135,58,0.12)",
              }}
            >
              <Text
                style={{ fontFamily: FONTS.bodySemiBold, fontSize: 12, color: LINEN }}
                numberOfLines={1}
              >
                {identity.fullName}
              </Text>
              {identity.orgLine ? (
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 10,
                    color: "rgba(237,232,223,0.30)",
                    marginTop: 1,
                  }}
                  numberOfLines={1}
                >
                  {identity.orgLine}
                </Text>
              ) : null}
              <Pressable
                onPress={() => {
                  closeDrawer();
                  void signOutToHome(router);
                }}
                style={[{ marginTop: 8, flexDirection: "row", alignItems: "center", gap: 5 }]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyMedium,
                    fontSize: 10.5,
                    color: "rgba(237,232,223,0.30)",
                  }}
                >
                  Sign out
                </Text>
              </Pressable>
            </View>
    </>
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: BLACK }} edges={["top", "left", "right"]}>
      <View style={{ flex: 1, flexDirection: "row" }}>
        {showSidebar ? (
          <View
            style={{
              width: SIDEBAR_W,
              backgroundColor: FOREST,
              borderRightWidth: STROKES.hairline,
              borderRightColor: "rgba(181,135,58,0.12)",
              flexDirection: "column",
            } as ViewStyle}
          >
            {sidebarBody}
          </View>
        ) : null}

        {/* Right column */}
        <View style={{ flex: 1, minWidth: 0, backgroundColor: BLACK }}>
          <View
            style={[
              {
                height: TOPBAR_H,
                paddingHorizontal: isMobile ? 12 : 24,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                borderBottomWidth: STROKES.hairline,
                borderBottomColor: BORDER,
                backgroundColor: "rgba(14,14,14,0.97)",
              },
              Platform.OS === "web"
                ? ({ backdropFilter: "blur(16px)" } as unknown as ViewStyle)
                : null,
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1, minWidth: 0 }}>
              {isMobile ? <HamburgerButton onPress={openDrawer} color={LINEN} /> : null}
              <Text
                style={{
                  fontFamily: FONTS.displayMedium,
                  fontSize: 17,
                  color: W,
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {topbarTitle}
              </Text>
            </View>
            <View
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: FOREST_MID,
                borderWidth: STROKES.hairline,
                borderColor: "rgba(181,135,58,0.30)",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {identity.avatarUrl ? (
                <Image
                  source={{ uri: identity.avatarUrl }}
                  style={{ width: 28, height: 28, borderRadius: 14 }}
                />
              ) : (
                <Text
                  style={{
                    fontFamily: FONTS.displaySemiBold,
                    fontSize: 12.5,
                    color: AMBER,
                  }}
                >
                  {identity.initials}
                </Text>
              )}
            </View>
          </View>

          <View style={{ flex: 1, minHeight: 0, backgroundColor: BLACK }}>{children}</View>
        </View>
      </View>

      <ShellMobileDrawer
        open={drawerOpen}
        onRequestClose={closeDrawer}
        backgroundColor={FOREST}
      >
        {sidebarBody}
      </ShellMobileDrawer>
    </SafeAreaView>
  );
}

/* ─────────────────────────  Sidebar primitive  ───────────────────────── */

function SidebarNavItem({
  label,
  icon,
  active,
  onPress,
}: {
  label: string;
  icon: ReactNode;
  active: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const isWebHover = Platform.OS === "web" && hover && !active;

  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 9,
          paddingHorizontal: 18,
          paddingVertical: SPACING.xs,
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? AMBER : "transparent",
          backgroundColor: active
            ? THEME.accentTintSoft
            : isWebHover
              ? THEME.neutralHoverBgOnDark
              : "transparent",
          marginBottom: 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ transition: "background-color 120ms, color 120ms" } as object)
          : null,
      ]}
    >
      <View style={{ opacity: active ? 1 : 0.5 }}>{icon}</View>
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.5,
          color: active ? LINEN : "rgba(237,232,223,0.42)",
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* ─────────────────────────  Icons  ───────────────────────── */

function IconClipboard({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Rect x={3} y={2.5} width={8} height={10} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={5} y={1.5} width={4} height={2} rx={0.5} stroke={color} strokeWidth={1.1} />
      <Path d="M5 6.5h4M5 8.5h4M5 10.5h2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconPerson({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={5} r={2.4} stroke={color} strokeWidth={1.1} />
      <Path
        d="M2.5 12c0-2.2 2-3.6 4.5-3.6S11.5 9.8 11.5 12"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    </Svg>
  );
}

function IconSettings({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={7} r={1.6} stroke={color} strokeWidth={1.1} />
      <Path
        d="M7 1.5v1.6M7 10.9v1.6M12.5 7h-1.6M3.1 7H1.5M10.9 3.1l-1.1 1.1M4.2 9.8l-1.1 1.1M10.9 10.9l-1.1-1.1M4.2 4.2L3.1 3.1"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    </Svg>
  );
}
